<template>
  <div>
    <drop-zone
      :linkage-id="adr.id"
      @picked="handleInputChange"
      @highlighted="onHighlight"
      :disabled="uploadDisabled"
      :class="[
        'dragField',
        {
          error: errorMsg,
          inReview,
          inProgress,
          highlighted,
        },
      ]"
    >
      <template v-if="highlighted">
        {{ $tc('bankStatementDropZone.dropFileText', adr.numberOfFilesRequired) }}
      </template>
      <template v-else>
        <template v-if="showUploadButton">
          <div class="uploadFileIcon">
            <svg-use name="Icons_OnDeck Brand_Universal_PDF_PDF" key="pdf" />
          </div>
          <div class="text">
            <span class="emphasize">{{ $t('bankStatementDropZone.dragText') }}</span>
            {{ $t('bankStatementDropZone.orText') }}
          </div>

          <input-file
            :id="`uploaded-files-${adr.id}`"
            class="inputFile"
            :linkage-id="adr.id"
            @picked="handleInputChange"
            accept="application/pdf"
            :multiple="adr.numberOfFilesRequired !== 1"
            small
            secondary
          >
            {{ $t('bankStatementDropZone.uploadButtonText') }}
          </input-file>
        </template>

        <div v-if="inProgress">
          <spinner class="loading" />
          <div class="text">
            {{ $t('bankStatementDropZone.uploadingText', { progress: uploadProgress }) }}
          </div>
        </div>

        <div v-show="inReview" class="successIcon">
          <svg-use name="Icons _ Action _ Check Circle" key="success" />
        </div>
        <div class="text" v-if="inReview">
          {{ $t('bankStatementDropZone.inReviewText') }}
        </div>
      </template>
    </drop-zone>

    <div class="details">{{ adr.requestDetails.details }}</div>

    <error-message v-if="errorMsg" class="errorMsg">
      <svg-use name="Icons _ Action _ Warning Circle" slot="icon" />
      <template #error>{{ errorMsg }}</template>
    </error-message>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import * as fileStates from '@/modules/upload-service/file-states';

const { IN_PROGRESS, ERROR, DELETE_ERROR } = fileStates;

export default {
  name: 'bank-statement-drop-zone',
  props: {
    adr: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      highlighted: false,
    };
  },
  computed: {
    ...mapGetters({
      filterFilesByQuery: 'file/filterFilesByQuery',
    }),
    filesForAdr() {
      return this.filterFilesByQuery({ adrId: this.adr.id });
    },
    filesInProgress() {
      return this.filesForAdr.filter(file => file.documentUploadStatus === IN_PROGRESS);
    },
    inProgress() {
      return !!this.filesInProgress.length;
    },
    uploadProgress() {
      const weightReducer = (accumulator, { progress = 0, fileObject: { size = 0 } = {} }) => {
        return progress * size + accumulator;
      };
      const sizeReducer = (accumulator, { fileObject: { size = 0 } = {} }) => size + accumulator;

      const sumOfWeights = this.filesInProgress.reduce(weightReducer, 0);
      const sumOfSizes = this.filesInProgress.reduce(sizeReducer, 0);

      return sumOfSizes ? Math.round(sumOfWeights / sumOfSizes) : 0;
    },
    uploadDisabled() {
      return this.adr.disabled || this.inProgress || this.inReview;
    },
    inReview() {
      const { numberOfFilesRequired } = this.adr;

      return this.filesForAdr.filter(file => file.isUploaded).length >= numberOfFilesRequired;
    },
    showUploadButton() {
      return !(this.inProgress || this.inReview);
    },
    errorFiles() {
      return this.filesForAdr.filter(
        file => file.documentUploadStatus === ERROR || file.documentUploadStatus === DELETE_ERROR,
      );
    },
    errorMsg() {
      return this.errorFiles.length ? this.errorFiles[0].errorMsg : '';
    },
  },
  methods: {
    handleInputChange(payload) {
      this.errorFiles.forEach(file => this.$emit('delete', file));
      this.$emit('picked', payload);
    },
    onHighlight(value) {
      this.highlighted = value;
    },
  },
};
</script>
<style lang="sass" scoped>
.dragField
  box-shadow: inset 0 0 0 1px $glass-color-system-neutral-grey-09
  border-radius: 4px
  align-items: center
  padding: $glass-spacing-3 $glass-spacing-5
  display: flex
  flex-direction: column
  +font-size(13.33)
  line-height: calc(20/14)
  &.error
    box-shadow: inset 0 0 0 1px $glass-color-system-validation-error-dark-03
  &.highlighted
    box-shadow: inset 0 0 0 2px $highlighted-border-drop-zone-partner-color
    background: $color-secondary-light-accessible-8
    color: $color-primary-brand-light
    font-weight: bold
    padding: baseunit(6.7) $glass-spacing-5
  &.inReview, &.inProgress
    padding: $glass-spacing-5 $glass-spacing-3
  .text
    color: $glass-color-system-neutral-grey-09
    margin-top: $glass-spacing-1
    .emphasize
      font-weight: bold
  .loading
    --spinner-width: #{baseunit(4)}
.inputFile
  margin-top: $glass-spacing-105

.uploadFileIcon
  color: $glass-color-system-neutral-grey-05

.successIcon
  color: $glass-color-system-validation-success-dark

.successIcon, .uploadFileIcon
  width: baseunit(4)
  height: baseunit(4)

.errorMsg
  justify-content: center

.details
  +font-size(13.33)
  font-weight: bold
  text-align: center
  margin: $glass-spacing-05 auto 0 auto
</style>
